import {useFormikContext} from 'formik';
import {
    Alert,
    MultiSelect,
    Stack,
    Text,
} from '@mantine/core';

import {useTestDefinitionsQuery} from '../../testing-test-definitions/hooks/useTestDefinitionsQuery.ts';
import {TestDefinitionStatus} from '../../testing-test-definitions/types.ts';
import {PersonaFormValues} from '../types.ts';

// The test definitions this persona runs on a wake (FNP-967).
//
// ⚠️ THIS is what the executor actually runs. `actionCodes` beside it is
// DEPRECATED (FNP-968 removes it) and is no longer read by the executor — it is
// retained only so the existing form keeps working through the transition.
//
// Options are the ACTIVE definitions, because only an ACTIVE definition is
// eligible to run. But a definition ALREADY SELECTED on this persona is always
// included even if it has since been retired: Mantine's MultiSelect silently
// drops a value with no matching option, so filtering strictly to ACTIVE would
// make a retired-but-still-attached definition vanish from the form and then be
// erased on the next save — a destructive edit the operator never asked for.
export const TestDefinitionsField = () => {

    const {
        values,
        setFieldValue,
        errors,
    } = useFormikContext<PersonaFormValues>();

    const {
        data: definitions = [],
        isLoading,
    } = useTestDefinitionsQuery();

    const selected = values.testDefinitionIds ?? [];

    const options = definitions
        .filter(definition =>
            definition.status === TestDefinitionStatus.ACTIVE ||
            selected.includes(definition.id))
        .map(definition => ({
            value: definition.id,
            // The testCode is the handle an operator recognises; the name is the
            // prose. Both, because a catalog of codes alone is unreadable.
            label: `${definition.testCode} — ${definition.name}${
                definition.status === TestDefinitionStatus.ACTIVE ? '' : ` (${definition.status})`
            }`,
        }));

    const fieldError = typeof errors.testDefinitionIds === 'string'
        ? errors.testDefinitionIds
        : undefined;

    return (
        <Stack gap={'sm'}>
            <MultiSelect
                aria-label='Test Definitions'
                label='Test Definitions'
                description='The tests this persona runs when it wakes. Only ACTIVE definitions are offered.'
                placeholder={selected.length === 0 ? 'Select test definitions' : undefined}
                data={options}
                value={selected}
                onChange={(value) => setFieldValue('testDefinitionIds', value)}
                disabled={isLoading}
                searchable
                clearable
                error={fieldError}
            />

            {!isLoading && options.length === 0 ? (
                <Alert color='gray' variant='light'>
                    No ACTIVE test definitions exist yet. Author one under Test
                    Definitions and activate it before assigning it to a persona.
                </Alert>
            ) : null}

            {selected.length === 0 ? (
                <Text size={'xs'} c={'dimmed'}>
                    A persona with no test definitions wakes and runs nothing.
                </Text>
            ) : null}
        </Stack>
    );
};
